import { produce } from "immer";
import { fetchAction, fetchActionPost, fetchPostApi, fetchApi } from "../../../helpers/fetch";
import { serialize } from "../../../helpers/serialize";
import { invalidateSubscriptionCaches } from "../../../helpers/cache";
import { GENRES } from "../../constants/genres";

// --- Feed list API (HYK-907: server-side category filter) ---

const FEED_REQUEST = "FETCH_CREATIONS_REQUEST";
const FEED_SUCCESS = "FETCH_CREATIONS_SUCCESS";
const FEED_FAILURE = "FETCH_CREATIONS_FAILURE";
const FEED_PAGINATE_REQUEST = "FETCH_CREATIONS_PAGINATE_REQUEST";
const FEED_PAGINATE_SUCCESS = "FETCH_CREATIONS_SUCCESS_PAGINATE";

/** True if this tab is a genre that the backend can filter by (not "all" or "subscriptions"). */
function getApiCategory(tab) {
  if (!tab || tab === "all" || tab === "subscriptions") return null;
  const lower = String(tab).toLowerCase();
  return Object.values(GENRES).includes(lower) ? lower : null;
}

/** Build query string for GET /api/creations. Backend should accept optional `category`. */
function buildFeedQuery(page, pageSize, category) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const apiCategory = getApiCategory(category);
  if (apiCategory) params.set("category", apiCategory);
  return params.toString();
}

export const fetchMarkSafe = (creation_id) => fetchPostApi(`/admin/mark-safe/${creation_id}`, {}, "PUT")
export const fetchAcceptReport = (creation_id) => fetchPostApi(`/admin/remove-creation/${creation_id}`, {}, "PUT")
export const fetchLikeCreation = (creation_id) => fetchPostApi(`/api/likes/${creation_id}/like`)
export const fetchUnLikeCreation = (creation_id) => fetchPostApi(`/api/likes/${creation_id}/unlike`)
export const fetchSaveUnSaveCreation = (creation_id, unsave = false) => fetchPostApi(`/api/creations/${creation_id}/save${unsave ? '?delete=true' : ''}`)
export const fetchReportCreation = (creation_id, reason) => fetchPostApi(`/api/report/submit`, { creation_id, reason })
// export const fetchSubscribe = (subscribed_to_id) => fetchPostApi(`/api/profile/subscribe`, { subscribed_to_id })
export const fetchDeleteCreation = (creation_id) => fetchPostApi(`/api/creations/${creation_id}`, {}, 'DELETE')
export const fetchAdminDeleteCreation = (creation_id) => fetchPostApi(`/admin/creations/${creation_id}`, {}, 'DELETE')

/** Fetch admin creations list with optional filters (page, limit, search, monetization, type, category, cultural_area) */
export const fetchAdminCreations = (params = {}) => {
  const query = serialize({
    page: params.page ?? 1,
    limit: params.limit ?? 10,
    ...(params.search && { search: params.search }),
    ...(params.monetization && params.monetization !== 'all' && { monetization: params.monetization }),
    ...(params.type && params.type !== 'all' && { type: params.type }),
    ...(params.category && params.category !== 'all' && { category: params.category }),
    ...(params.cultural_area && params.cultural_area !== 'all' && { cultural_area: params.cultural_area }),
    ...(params.sort_by && { sort_by: params.sort_by }),
    ...(params.sort_dir && { sort_dir: params.sort_dir }),
  });
  return fetchApi(`/admin/creations?${query}`);
};

/** Fetch admin creation detail by id */
export const fetchAdminCreationDetail = (creation_id) =>
  fetchApi(`/admin/creations/${creation_id}`);

export const postCreation = (creation) => fetchPostApi(`/api/creations/create_creation`, creation)
export const updateCreation = (creation_id, creation) => fetchPostApi(`/api/creations/${creation_id}`, creation, 'PUT')

export const deleteFromLibrary = (creation_id) => fetchPostApi(`/api/library/remove-from-library`, { creation_id }, 'DELETE')
export const addToLibrary = (creation_id) => fetchPostApi(`/api/library/add-to-library`, { creation_id })


export const fetchReports = () => fetchAction('/admin/reported-creations', [
    'SET_REPORTS_REQUEST',
    'SET_REPORTS_SUCCESS',
    'SET_REPORTS_FAILURE'
])

export const fetchCreationComments = (news_id) =>
    fetchAction(`/api/creations/${news_id}/comments`, [
        "FETCH_CREATION_COMMENTS_REQUEST",
        "FETCH_CREATION_COMMENTS_SUCCESS",
        "FETCH_CREATION_COMMENTS_FAILURE",
    ]);

export const updateReports = (_reports, creation_id, fields) => {
    const updatedReports = produce(_reports, (draft) => {
        const reports = draft.filter((r) => r.creation_id === creation_id);
        if (reports && reports.length > 0) {
            reports.forEach((report, index) => {
                reports[index] = Object.assign(report, fields)
            })
        }
    });

    return {
        type: "UPDATE_REPORTS",
        payload: updatedReports
    };
};

/**
 * Fetch first page of feed. Category filter is sent to the API so the backend
 * can return only matching creations (HYK-907). Pass activeTab as category.
 */
export const fetchAllCreations = (page = 1, pageSize = 10, category = null) => {
  const query = buildFeedQuery(page, pageSize, category);
  const url = `/api/creations?${query}`;
  return async (dispatch) => {
    dispatch({ type: FEED_REQUEST, payload: url });
    try {
      const response = await fetchApi(url);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch creations");
      }
      const data = Array.isArray(response.data) ? response.data : [];
      const requestedCategory = getApiCategory(category);
      dispatch({
        type: FEED_SUCCESS,
        payload: { data, requestedCategory },
      });
      return { success: true, response };
    } catch (error) {
      dispatch({ type: FEED_FAILURE, payload: error });
      return { success: false, response: error };
    }
  };
};

/**
 * Fetch next page for infinite scroll. Use same category as initial load.
 */
export const fetchAllCreationsPaginate = (page, pageSize = 10, category = null) => {
  const query = buildFeedQuery(page, pageSize, category);
  const url = `/api/creations?${query}`;
  return async (dispatch) => {
    dispatch({ type: FEED_PAGINATE_REQUEST });
    try {
      const response = await fetchApi(url);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to fetch creations");
      }
      const data = Array.isArray(response.data) ? response.data : [];
      const requestedCategory = getApiCategory(category);
      dispatch({
        type: FEED_PAGINATE_SUCCESS,
        payload: { data, requestedCategory },
      });
      return { success: true, response };
    } catch (error) {
      dispatch({ type: FEED_FAILURE, payload: error });
      return { success: false, response: error };
    }
  };
};

export const fetchSearchCreations = (query) => fetchApi(`/api/creations/search?query=${encodeURIComponent(query)}`);
export const fetchSearchLibraryCreations = (query) => fetchApi(`/api/library/search?query=${encodeURIComponent(query)}`);

export const fetchAddCommentCreation = (bodyContent) => fetchActionPost(`/api/comment/submit`, bodyContent, [
    'ADD_COMMENT_REQUEST',
    'ADD_COMMENT_SUCCESS',
    'ADD_COMMENT_FAILURE'
])


// Fetch library creations with pagination
export const fetchLibraryCreations = (page = 1, pageSize = 20) => fetchAction(`/api/library?page=${page}&pageSize=${pageSize}`, [
    'FETCH_LIBRARY_CREATIONS_REQUEST',
    'FETCH_LIBRARY_CREATIONS_SUCCESS',
    'FETCH_LIBRARY_CREATIONS_FAILURE'
]);

// Fetch user creations with pagination
export const fetchUserCreations = (userId, page = 1, pageSize = 20) => fetchAction(`/api/creations/${userId}?page=${page}&pageSize=${pageSize}`, [
    'FETCH_USER_CREATIONS_REQUEST',
    'FETCH_USER_CREATIONS_SUCCESS',
    'FETCH_USER_CREATIONS_FAILURE'
]);

// Fetch more user creations for pagination
export const fetchMoreUserCreations = (userId, page, pageSize = 20) => fetchAction(`/api/creations/${userId}?page=${page}&pageSize=${pageSize}`, [
    'FETCH_MORE_USER_CREATIONS_REQUEST',
    'FETCH_MORE_USER_CREATIONS_SUCCESS',
    'FETCH_MORE_USER_CREATIONS_FAILURE'
]);

export const setLoadingCreation = (loading) => {
    return {
        type: 'SET_LOADING_CREATION',
        payload: loading
    }
}
// Fetch filtered creations for a user
export const filterUserCreations = (userId, filter) => fetchAction(`/api/creations/${userId}/filter/${filter}`, [
    'FILTER_CREATIONS_REQUEST',
    'FILTER_CREATIONS_SUCCESS',
    'FILTER_CREATIONS_FAILURE'
]);

// Fetch a specific creation by ID
export const fetchCreationById = (creationId) => fetchAction(`/api/creations/detail/${creationId}`, [
    'FETCH_CREATION_DETAIL_REQUEST',
    'FETCH_CREATION_DETAIL_SUCCESS',
    'FETCH_CREATION_DETAIL_FAILURE'
]);


// Fouzi
export const setCreations = (creations) => {
    return {
        type: 'SET_CREATIONS',
        payload: creations
    }
}

export const setActiveTab = (tab) => {
    return {
        type: 'SET_ACTIVE_TAB',
        payload: tab
    }
}

export const setViewCreation = (creation) => {
    return {
        type: 'SET_VIEW_CREATION',
        payload: creation
    }
}
export const setViewUser = (user) => {
    return {
        type: 'SET_VIEW_USER',
        payload: user
    }
}
export const resetCreations = () => {
    return {
        type: 'RESET_CREATIONS'
    }
}
export const filterCreations = (state) => {
    const { creations, activeTab, feedRequestedCategory } = state.creation;
    const reports = state.creation.reports || [];
    const user = state.auth.user;

    const reportedIds = (reports || [])
        .filter((r) => r.status === "accepted")
        .map((r) => r.creation_id);
    const list = (creations || []).filter((c) => !reportedIds.includes(c.creation_id));

    // List was fetched for this category from API; no client-side category filter
    if (feedRequestedCategory && activeTab === feedRequestedCategory) {
        return list;
    }

    if (activeTab === "all" || !activeTab) return list;
    if (activeTab === "subscriptions") {
        const subIds = new Set((user?.subscriptions || []).map((s) => s.user_id));
        return list.filter((c) => subIds.has(c.creator_id));
    }
    const tabLower = String(activeTab).toLowerCase();
    return list.filter((c) => (c.category || "").toLowerCase() === tabLower);
}

export const filterMyCreations = (state) => {
    const { my_creations, activeTab } = state.creation;
    const reports = state.creation.reports || [];

    // Get creation IDs that have reports with status "under review" or "accepted"
    const reportedCreationIds = reports
        .filter(report => report.status === 'under review' || report.status === 'accepted')
        .map(report => report.creation_id);

    // Filter out reported creations
    const nonReportedCreations = (my_creations || []).filter(creation =>
        !reportedCreationIds.includes(creation.creation_id)
    );

    // Apply category filter
    const filteredCreations = nonReportedCreations.filter(creation => {
        if (activeTab === 'all' || activeTab === 'All' || !activeTab) return true;
        return creation.category?.toLowerCase() === activeTab.toLowerCase();
    });

    // Sort by recency (most recent first)
    return filteredCreations.sort((a, b) => {
        const dateA = new Date(a.created_at || a.updated_at || 0);
        const dateB = new Date(b.created_at || b.updated_at || 0);
        return dateB - dateA;
    });
}

export const getFilteredCreationsOfUser = (state) => {
    const { activeTab } = state.creation
    const creations = state.creation.view_user?.creations || []
    const reports = state.creation.reports || []

    // Get creation IDs that have reports with status "under review" or "accepted"
    const reportedCreationIds = reports
        .filter(report => report.status === 'under review' || report.status === 'accepted')
        .map(report => report.creation_id)

    // Filter out reported creations
    const nonReportedCreations = creations.filter(creation =>
        !reportedCreationIds.includes(creation.creation_id)
    )

    // Apply category filter
    const filteredCreations = nonReportedCreations.filter(creation => {
        if (activeTab === 'all' || !activeTab) return true;
        return creation.category?.toLowerCase() === activeTab.toLowerCase();
    })

    // Sort by recency (most recent first)
    return filteredCreations.sort((a, b) => {
        const dateA = new Date(a.created_at || a.updated_at || 0);
        const dateB = new Date(b.created_at || b.updated_at || 0);
        return dateB - dateA;
    })
}

export const likeUnlikeCreation = (creation_id, isLike) => {
    return {
        type: 'SET_LIKE_SAVE_CREATION',
        payload: {
            creation_id,
            isLike,
            actioned: 'like',
        },
    };
};


export const saveUnSaveCreation = (creation_id, isSave) => {
    return {
        type: 'SET_LIKE_SAVE_CREATION',
        payload: {
            creation_id,
            isSave,
            actioned: 'save',
        },
    };
};


export const searchCreations = (query, library=false) => {
    return async (dispatch) => {
        dispatch({ type: 'SEARCH_CREATIONS_REQUEST' });

        try {
            const response = library ? await fetchSearchLibraryCreations(query) : await fetchSearchCreations(query);
            if (response.success) {
                dispatch({
                    type: 'SEARCH_CREATIONS_SUCCESS',
                    payload: response.data
                });
                return { success: true, data: response.data };
            } else {
                dispatch({
                    type: 'SEARCH_CREATIONS_FAILURE',
                    payload: response.message || 'Search failed'
                });
                return { success: false, message: response.message };
            }
        } catch (error) {
            dispatch({
                type: 'SEARCH_CREATIONS_FAILURE',
                payload: 'Failed to search creations'
            });
            return { success: false, message: 'Failed to search creations' };
        }
    };
};


// Fetch saved creations by user ID
export const fetchSavedCreationsByUser = (userId) => fetchAction(`/api/creations/${userId}/saved`, [
    'FETCH_SAVED_CREATIONS_REQUEST',
    'FETCH_SAVED_CREATIONS_SUCCESS',
    'FETCH_SAVED_CREATIONS_FAILURE'
]);




export const addCreationToLibrary = (creationId) => fetchPostApi(`/api/creations/${creationId}/save`, {}, "POST")


export const fetchUserById = (user_id, include_creations, include_news, include_subscribers = false, include_subscriptions = false, include_addresses = false) => fetchAction(`/api/profile/${user_id}?${serialize({ include_creations, include_news, include_subscribers, include_subscriptions, include_addresses })}`, [
    'SET_VIEWUSER_REQUEST',
    'SET_VIEWUSER_SUCCESS',
    'SET_VIEWUSER_FAILURE'
])

// Fetch subscribers separately on demand
export const fetchUserSubscribers = (user_id) => fetchAction(`/api/profile/${user_id}?${serialize({ include_subscribers: true })}`, [
    'FETCH_USER_SUBSCRIBERS_REQUEST',
    'FETCH_USER_SUBSCRIBERS_SUCCESS',
    'FETCH_USER_SUBSCRIBERS_FAILURE'
])

// Fetch subscriptions separately on demand
export const fetchUserSubscriptions = (user_id) => fetchAction(`/api/profile/${user_id}?${serialize({ include_subscriptions: true })}`, [
    'FETCH_USER_SUBSCRIPTIONS_REQUEST',
    'FETCH_USER_SUBSCRIPTIONS_SUCCESS',
    'FETCH_USER_SUBSCRIPTIONS_FAILURE'
])

// Fetch addresses separately on demand
export const fetchUserAddresses = (user_id) => fetchAction(`/api/profile/${user_id}?${serialize({ include_addresses: true })}`, [
    'FETCH_USER_ADDRESSES_REQUEST',
    'FETCH_USER_ADDRESSES_SUCCESS',
    'FETCH_USER_ADDRESSES_FAILURE'
])


// Resolves { success, response, alreadySubscribed }. alreadySubscribed is true when the backend
// found an existing follow (HYK-1138): nothing was created, so callers must not treat it as a
// new subscription (e.g. the HYK-1109 auto-Contribute prompt).
export const fetchSubscribe = (subscribed_to_id, subscribe = true) => async (dispatch) => {
    const result = await dispatch(fetchActionPost(`/api/profile/subscribe`, { subscribed_to_id, subscribe },
        [
            'SUBSCRIBE_REQUEST',
            '',
            'SUBSCRIBE_FAILURE'
        ]
    ));
    invalidateSubscriptionCaches();
    return { ...result, alreadySubscribed: Boolean(result?.response?.data?.already_subscribed) };
}

// Search overlay actions (openSearchOverlay and closeSearchOverlay removed - replaced by unified search page)
export const setSearchOverlayQuery = (query) => ({ type: 'SET_SEARCH_OVERLAY_QUERY', payload: query });

export const enterSearchMode = () => ({ type: 'ENTER_SEARCH_MODE' });
export const exitSearchMode = () => ({ type: 'EXIT_SEARCH_MODE' });

export const resetSearchState = () => ({ type: 'RESET_SEARCH_STATE' });

// Search tab actions
export const setSearchActiveTab = (tab) => ({ type: 'SET_SEARCH_ACTIVE_TAB', payload: tab });

export const signalAddCreationSuccess = (value) => ({
    type: 'SIGNAL_ADD_CREATION_SUCCESS',
    payload: value
})
// export const reportCreation = (user_id, creation_id, reason, creations, currentCreation) => {

//     try {
//         // Update the creation in the list if it exists
//         if (creations?.length > 0) {
//             const creationIndex = creations.findIndex(creation => creation.creation_id === creation_id);
//             if (creationIndex !== -1) {
//                 const updatedCreations = [...creations];
//                 updatedCreations[creationIndex] = {
//                     ...updatedCreations[creationIndex],
//                     user_report: { status: 'under review' },
//                 };
//                 dispatch(setCreations(updatedCreations));
//             }
//         }


//         // Update current creation if it matches
//         if (currentCreation && currentCreation.creation_id === creation_id) {
//             dispatch(setViewCreation({
//                 ...currentCreation,
//                 user_report: { status: 'under review' },
//             }));
//         }
//     } catch (error) {
//         console.error("Error reporting creation:", error);
//     }

// };



export const updateReportOnCreation = (creation_id) => ({
    type: 'UPDATE_REPORTED_CREATION',
    payload: { creation_id }
})


export const reloadCreation = (value) => ({
    type: 'RELOAD_CREATION',
    payload: value
})